import { Button, Flex } from '@radix-ui/themes';
import { CommonCallout } from 'components/common/callouts';
import { ErrorBoundary } from 'components/common/error-boundary';
import env from 'config';
import useLatest from 'hooks/useLatestRef';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { useEffect, useState } from 'react';

// how long to wait before proceeding automatically, for local testing make it outrageously long to prove the point
const TIMEOUT_SEC = env.identifier === 'local' ? 100 : 10;

export const PTSeekFailureCallout = (props: {
  onRetrain: () => void;
  onContinue: () => void;
}) => {
  const [count, setCount] = useState(TIMEOUT_SEC);
  const latestCount = useLatest(count);

  const [counting, setCounting] = useState(true);

  const latestOnContinue = useLatest(props.onContinue);

  // default countdown behaviour => proceed as quick trained
  useEffect(() => {
    if (!counting) {
      return;
    }

    const interval = setInterval(() => {
      const nextCount = latestCount.current - 1;

      if (nextCount === 0) {
        // default action on timeout
        latestOnContinue.current();
      }

      if (nextCount <= 0) {
        // stop counting after reaching or passing 0
        setCounting(false);
      }

      // console.debug(`*** setting count to ${nextCount}`);
      setCount((prev) => prev - 1);
    }, 1_000);

    return () => clearInterval(interval);
  }, [counting]);

  return (
    <ErrorBoundary identifier="PTSeekFailureCallout">
      <CommonCallout
        flexAlign="start"
        content={
          <Flex
            direction="column"
            onClick={
              // stop the countdown
              () => setCounting(false)
            }
            onTouchMove={
              // stop the countdown
              () => setCounting(false)
            }
          >
            <p>
              We were unable to train the pitch within the desired thresholds;
              it has been quick trained instead. You may choose to retrain this
              pitch using the button below.
            </p>

            <Flex mt="3" gap="2" justify="end">
              <Button
                variant="soft"
                className="width-120px"
                color={RADIX.COLOR.NEUTRAL}
                onClick={() => props.onRetrain()}
              >
                Retrain
              </Button>

              <Button
                className="width-120px"
                color={RADIX.COLOR.ACCENT}
                onClick={() => props.onContinue()}
              >
                Continue {counting ? `(${count}s)` : undefined}
              </Button>
            </Flex>
          </Flex>
        }
      />
    </ErrorBoundary>
  );
};
